import { Controller } from '@hotwired/stimulus'

import { connectUserData, getFlow } from '../helpers/controllerHelpers/utils'
import renderTemplate from '../api/helpers/templating'

import type { UserData } from '../api/models/types'

const DEFAULT_LANGUAGE = 'en'

export default class extends Controller {
  static values = {
    text: String,
    description: String,
    alt_text: String,
    label: String,
    sublabel: String,
    off_label: String,
    caption: String,
    src: String,
    placeholder: String,
  }

  declare textValue: string | null
  declare descriptionValue: string | null
  declare altTextValue: string | null
  declare labelValue: string | null
  declare sublabelValue: string | null
  declare offLabelValue: string | null
  declare captionValue: string | null
  declare srcValue: string | null
  declare placeholderValue: string | null

  declare disconnectUserData: (() => void) | undefined
  declare userDataValue: UserData

  connect() {
    //* Connect the user data to the controller
    this.disconnectUserData = connectUserData(this, this.userDataValueChanged)

    //* Trigger the initial update
    this.updateContentBaseOnLanguage()
  }

  disconnect() {
    if (typeof this.disconnectUserData === 'function') this.disconnectUserData()
  }

  userDataValueChanged() {
    this.updateContentBaseOnLanguage()
  }

  updateContentBaseOnLanguage() {
    const language = this.getEmbeddableLanguage()
    const langPrefix = `lang--${language}--`

    const attrs = Array.from(this.element.attributes)

    //* Define the attributes with their render logic
    const attributeConfig = {
      text: {
        value: this.textValue,
        render: (text: string) => {
          this.element.innerHTML = text
        },
      },
      description: {
        value: this.descriptionValue,
        render: (text: string) => {
          this.element.innerHTML = text
        },
      },
      alt_text: {
        value: this.altTextValue,
        render: (text: string) => {
          if (this.element.hasAttribute('alt')) {
            this.element.setAttribute('alt', text)
          } else {
            this.element.setAttribute('data-alt', text)
          }
        },
      },
      label: {
        value: this.labelValue,
        render: (text: string) => {
          this.element.innerHTML = text
        },
      },
      sublabel: {
        value: this.sublabelValue,
        render: (text: string) => {
          this.element.innerHTML = text
        },
      },
      placeholder: {
        value: this.placeholderValue,
        render: (text: string) => {
          this.element.innerHTML = text
          if (this.element.hasAttribute('placeholder')) {
            this.element.setAttribute('placeholder', text)
          } else {
            this.element.setAttribute('data-placeholder', text)
          }
        },
      },
      off_label: {
        value: this.offLabelValue,
        render: (text: string) => {
          this.element.innerHTML = text
        },
      },
      caption: {
        value: this.captionValue,
        render: (text: string) => {
          this.element.innerHTML = text
        },
      },
      src: {
        value: this.srcValue,
        render: (src: string) => {
          if (this.element.hasAttribute('src')) {
            this.element.setAttribute('src', src)
          }
          if (this.element.hasAttribute('data-src')) {
            this.element.setAttribute('data-src', src)
          }
        },
      },
    }

    //* Find all attribute types that have values defined
    const attributeTypes: (keyof typeof attributeConfig)[] = []

    //* Find all attribute types that have values defined
    for (const [key, config] of Object.entries(attributeConfig)) {
      if (config.value) {
        attributeTypes.push(key as keyof typeof attributeConfig)
      }
    }

    if (attributeTypes.length === 0) return

    //* Process each attribute type (i.e. text, description, etc.)
    attributeTypes.forEach((attributeType) => {
      //* Find the translated attribute for this specific type
      const translatedAttr = attrs.find(
        (a) => a.name.startsWith(langPrefix) && a.name.endsWith(`-${attributeType}`)
      )

      //* Get the translated value for this attribute type
      const translated =
        (translatedAttr && translatedAttr.value) || attributeConfig[attributeType].value

      //* Render the template for the translated value
      const text = renderTemplate(translated || '', this.userDataValue)

      if (!text) return

      //* Apply the specific render logic for this attribute type
      attributeConfig[attributeType].render(text)
    })

    //* Dispatch an event to the template controller to force an update
    this.element.dispatchEvent(new CustomEvent('language:updated', { bubbles: true }))
  }

  getEmbeddableLanguage() {
    const userData = this.userDataValue || {}
    const userDataLanguage = userData.embeddable_language as string || DEFAULT_LANGUAGE

    const allowedLanguageKeys = this.getEmbeddableAllowedLanguageKeys()
    if (!allowedLanguageKeys.includes(userDataLanguage)) return DEFAULT_LANGUAGE

    return userDataLanguage
  }


  getEmbeddableAllowedLanguageKeys() {
    const flow = getFlow(this)
    const allowedKeys = flow?.languages?.map((lang) => lang.key).filter(Boolean) ?? []
    return allowedKeys
  }
}
